import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { filter, Observable, of, switchMap, tap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import {
    AppSourceUrlMap,
    Content,
    LOCAL_BRAND,
    MOBILIAR,
    PAGE_URL_PATH,
    PROTEKTA,
    TealiumTrackingFacade,
    WINDOW,
    oivCoreSharedActions,
    oivCoreSharedFeature,
} from '@mobi/oiv-viewer-utils-ng-jslib';
import { OivXmlParserService } from '@mobi/oiv-viewer-xml-parser-ng-jslib';

import { Helper } from '../../../../share/helper';
import { Avb, Index } from '../../../../share/model/atomic-model';
import { filterTruthy } from '../../../../util/filter-truthy';
import { OivViewerService } from '../service/oiv-viewer.service';

import { oivViewerActions, oivViewerApprovalActions } from './oiv-viewer.actions';
import { fromOivViewer } from './oiv-viewer.selectors';

@Injectable()
export class OivViewerEffects {
    #store = inject(Store);
    #localBrand = inject(LOCAL_BRAND);

    #actions$ = inject(Actions) as unknown as Observable<Action>;
    #oiXmlParserService = inject(OivXmlParserService);
    #oivViewerService = inject(OivViewerService);
    #helper = inject(Helper);
    #router = inject(Router);
    #window = inject(WINDOW);
    #tealiumTrackingFacade = inject(TealiumTrackingFacade);
    #appContext = this.#store.selectSignal(oivCoreSharedFeature.selectContext);
    #appSource = this.#store.selectSignal(oivCoreSharedFeature.selectSource);

    $languageChanged = createEffect(() =>
        this.#actions$.pipe(
            ofType(oivCoreSharedActions.changeLanguage),
            filter(
                () =>
                    !this.#router.url.includes(PAGE_URL_PATH.NOT_FOUND) &&
                    !this.#router.url.includes(PAGE_URL_PATH.NOT_AVAILABLE) &&
                    !this.#router.url.includes(PAGE_URL_PATH.NO_PERMISSION),
            ),
            concatLatestFrom(() => [
                this.#store.select(fromOivViewer.selectUrlName),
                this.#store.select(oivCoreSharedFeature.selectIsApprovalMode),
            ]),
            switchMap(([, urlName, isApproval]) => {
                if (isApproval) {
                    return of(oivViewerApprovalActions.load({ urlName }));
                }
                return of(oivViewerActions.load({ urlName }));
            }),
        ),
    );

    load$ = createEffect(() =>
        this.#actions$.pipe(
            ofType(oivViewerActions.load, oivViewerApprovalActions.load),
            concatLatestFrom(() => this.#store.select(oivCoreSharedFeature.selectLanguage)),
            switchMap(([{ type, urlName }, language]) => {
                let isApprovalMode = false;
                if (type === oivViewerApprovalActions.load.type) {
                    isApprovalMode = true;
                }
                return this.#oivViewerService.getXml(urlName, language, isApprovalMode).pipe(
                    map((avb: Avb) => {
                        // Redirect if wrong product of current sourceType
                        // Check if Protekta based on Product Art
                        const isBrandOverrideActive = this.#localBrand !== undefined;
                        if (
                            !isBrandOverrideActive &&
                            this.#appSource() === MOBILIAR &&
                            (avb.productArt === 20003 || avb.productArt === 20521)
                        ) {
                            this.#store.dispatch(oivViewerActions.redirectToAppSource({ sourceType: PROTEKTA }));
                            return null;
                        }
                        if (
                            !isBrandOverrideActive &&
                            this.#appSource() === PROTEKTA &&
                            avb.productArt !== 20003 &&
                            avb.productArt !== 20521
                        ) {
                            this.#store.dispatch(oivViewerActions.redirectToAppSource({ sourceType: MOBILIAR }));
                            return null;
                        }
                        return avb;
                    }),
                    filterTruthy,
                    tap(({ textReference }: Avb) => {
                        if (textReference) {
                            this.#store.dispatch(oivViewerActions.loadFeatures({ textReference }));
                        }
                    }),
                    map((avb: Avb) => {
                        let avbChapters: Content[] = [];
                        let avbCoverSheet: Content = {} as Content;
                        if (avb.xmlContent) {
                            this.#oiXmlParserService.parseOivXml(avb.xmlContent);
                            const oivTree = this.#oiXmlParserService.getAllChapters();
                            const mappingContents = this.#helper.mappingToContents(oivTree);
                            avbChapters = this.#helper.getChapters(mappingContents);
                            avbCoverSheet = this.#helper.getCoverSheet(mappingContents);
                        }
                        // Remove the xmlContent from the avb object because we parse it into the avbChapters
                        avb.xmlContent = '';

                        this.#tealiumTrackingFacade.addVolatile('language_code', language);
                        this.#tealiumTrackingFacade.addVolatile('user_loginStatus', 'true');

                        return oivViewerActions.loaded({
                            avbMetadata: avb,
                            avbChapters,
                            avbCoverSheet,
                        });
                    }),
                    catchError(error => {
                        return of(oivViewerActions.loadFailure({ error }));
                    }),
                );
            }),
        ),
    );

    loaded$ = createEffect(() =>
        this.#actions$.pipe(
            ofType(oivViewerActions.loaded),
            concatLatestFrom(() => this.#store.select(fromOivViewer.selectAvbMetaData)),
            switchMap(([, avb]: [unknown, Avb]) => {
                return of(
                    oivViewerActions.loadIndex({
                        textReference: avb.textReference,
                        assetType: avb.assetType || '',
                        contentVersion: avb.contentVersion,
                    }),
                );
            }),
        ),
    );

    redirectToApp$ = createEffect(
        () =>
            this.#actions$.pipe(
                ofType(oivViewerActions.redirectToAppSource),
                tap(({ sourceType }) => {
                    // THIS IS FOR DEVELOPMENT PURPOSES ONLY
                    // Don't redirect if running on localhost - allow local development with hardcoded brands
                    const hostname = this.#window.location.hostname;
                    if (hostname === 'localhost' || hostname === '127.0.0.1') {
                        return;
                    }

                    const isProduct = (this.#window.document.defaultView as Window).MOBI_RWC_CONFIG?.stage === 'prod';
                    // B2E | B2C
                    const appContext = this.#appContext();
                    const baseUrl = isProduct
                        ? AppSourceUrlMap[sourceType][appContext].product
                        : AppSourceUrlMap[sourceType][appContext].preproduct;
                    this.#window.location.href = baseUrl + this.#router.url;
                }),
            ),
        { dispatch: false },
    );

    loadFailed$ = createEffect(
        () =>
            this.#actions$.pipe(
                ofType(oivViewerActions.loadFailure),
                concatLatestFrom(() => this.#store.select(oivCoreSharedFeature.selectLanguage)),
                tap(([error, language]: [{ error: { status: number } }, string]) => {
                    this.#store.dispatch(oivViewerActions.refresh());
                    if (error.error.status === 406) {
                        if (this.#appContext() === 'B2C' && this.#appSource() === MOBILIAR) {
                            this.#router.navigate([`/${PAGE_URL_PATH.NOT_AVAILABLE}`]);
                        } else {
                            this.#router.navigate([`/${language}/${PAGE_URL_PATH.NOT_AVAILABLE}`]);
                        }
                    } else {
                        if (this.#appContext() === 'B2C' && this.#appSource() === MOBILIAR) {
                            this.#router.navigate([`/${PAGE_URL_PATH.NOT_FOUND}`]);
                        } else {
                            this.#router.navigate([`/${language}/${PAGE_URL_PATH.NOT_FOUND}`]);
                        }
                    }
                }),
            ),
        { dispatch: false },
    );

    $loadIndex = createEffect(() =>
        this.#actions$.pipe(
            ofType(oivViewerActions.loadIndex),
            concatLatestFrom(() => this.#store.select(oivCoreSharedFeature.selectLanguage)),
            switchMap(([{ textReference, assetType, contentVersion }, language]) => {
                return this.#oivViewerService.getIndices(textReference, language, assetType, contentVersion).pipe(
                    map((index: Index) => {
                        return oivViewerActions.loadedIndex({ index });
                    }),
                    catchError(error => {
                        return of(oivViewerActions.loadIndexFailure({ error }));
                    }),
                );
            }),
        ),
    );
}
